import React, { useState, useEffect } from 'react';
import { X, Send, ExternalLink, Copy, Check, MessageSquare, Phone, User, FileText, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

export default function SendWhatsAppModal({
  isOpen,
  onClose,
  title = 'Send via WhatsApp',
  documentNumber = '',
  partyName = '',
  defaultMobile = '',
  defaultMessage = '',
  infoBadges = [],
}) {
  const { toast } = useToast();
  const [mobile, setMobile] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setMobile(defaultMobile || '');
    setMessage(defaultMessage || '');
    setCopied(false);
    setSending(false);
  }, [isOpen, defaultMobile, defaultMessage]);

  if (!isOpen) return null;

  // Format mobile for WhatsApp link (strip non-digits, ensure country code if 10 digits)
  const getCleanMobile = (num) => {
    const digits = (num || '').replace(/\D/g, '');
    if (digits.length === 10) return `91${digits}`;
    return digits;
  };

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast({ title: 'Copied to clipboard', description: 'WhatsApp message copied successfully' });
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      toast({ title: 'Could not copy', variant: 'destructive' });
    }
  };

  const handleOpenWhatsAppWeb = () => {
    const cleanNum = getCleanMobile(mobile);
    if (!cleanNum) {
      toast({ title: 'Recipient phone number is required', variant: 'destructive' });
      return;
    }
    const encoded = encodeURIComponent(message);
    const waUrl = `https://web.whatsapp.com/send?phone=${cleanNum}&text=${encoded}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
  };

  const handleSendViaGateway = async () => {
    const cleanNum = (mobile || '').trim();
    if (!cleanNum) {
      toast({ title: 'Recipient mobile number is required', variant: 'destructive' });
      return;
    }
    if (!message.trim()) {
      toast({ title: 'Message text cannot be empty', variant: 'destructive' });
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${backendUrl}/api/whatsapp/send`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          mobile: cleanNum,
          custom_text: message,
          customer_name: partyName || 'Customer',
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.detail || data.message || 'WhatsApp gateway failed to dispatch message');
      }

      toast({
        title: 'WhatsApp Sent Successfully',
        description: data.message || `Message dispatched to ${cleanNum}`,
      });
      onClose();
    } catch (err) {
      toast({
        title: 'WhatsApp Gateway Error',
        description: `${err.message}. You can still use 'Open WhatsApp Web' to send directly.`,
        variant: 'destructive',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg border border-gray-100 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-700 p-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-2 bg-white/15 rounded-lg">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold font-manrope leading-tight">{title}</h2>
              <p className="text-xs text-emerald-100 font-ibm">
                {documentNumber ? `Quotation ${documentNumber}` : 'Logistics quotation message'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          
          {/* Quick info badges */}
          {(partyName || infoBadges.length > 0) && (
            <div className="bg-emerald-50/70 border border-emerald-100 rounded-lg p-3 text-xs flex flex-wrap items-center gap-2">
              {partyName && (
                <div className="flex items-center gap-1 font-semibold text-emerald-950 font-ibm">
                  <User className="w-3.5 h-3.5 text-emerald-600" />
                  <span>{partyName}</span>
                </div>
              )}
              {infoBadges.map((b, i) => (
                <span key={i} className="bg-white px-2 py-0.5 rounded border border-emerald-200 text-emerald-800 font-ibm">
                  <strong className="text-emerald-900">{b.label}:</strong> {b.value}
                </span>
              ))}
            </div>
          )}

          {/* Recipient Phone */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-text-dark font-ibm flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <Phone className="w-3.5 h-3.5 text-emerald-600" /> Recipient Mobile Number
              </span>
              <span className="text-[11px] font-normal text-text-secondary">e.g. 9876543210 or 919876543210</span>
            </label>
            <Input
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="Enter mobile number"
              className="font-ibm text-sm"
              data-testid="wa-recipient-phone"
            />
          </div>

          {/* Message Text */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-text-dark font-ibm flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5 text-emerald-600" /> WhatsApp Message Body
              </label>
              <button
                type="button"
                onClick={handleCopyMessage}
                className="text-[11px] text-emerald-700 hover:text-emerald-900 font-ibm flex items-center gap-1"
              >
                {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              className="font-ibm text-xs leading-relaxed font-mono bg-gray-50/50 resize-y"
              data-testid="wa-message-textarea"
            />
            <p className="text-[11px] text-text-secondary font-ibm">
              Supports WhatsApp formatting: <code className="text-xs bg-gray-100 px-1 rounded">*bold*</code>, <code className="text-xs bg-gray-100 px-1 rounded">_italic_</code>.
            </p>
          </div>

        </div>

        {/* Footer Actions */}
        <div className="bg-gray-50 px-5 py-3 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-text-secondary font-ibm text-xs"
          >
            Cancel
          </Button>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleOpenWhatsAppWeb}
              className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-ibm text-xs flex items-center gap-1.5"
              title="Open WhatsApp Web in a new tab"
              data-testid="wa-open-web-btn"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open WhatsApp Web
            </Button>

            <Button
              type="button"
              size="sm"
              onClick={handleSendViaGateway}
              disabled={sending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-ibm text-xs flex items-center gap-1.5"
              data-testid="wa-send-api-btn"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              Send via API
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
}
